import { IScheduledInterviewsConfig, ITag, OptionStatusType, RegionConfigType, SectionStatusType } from '../index.js';
import { IDocumentConfig } from './documents/index.js';
import { IBeforeArrivalDocumentsConfig } from './before-arrival-documents/index.js';
import { IAfterArrivalDocumentsConfig } from './after-arrival-documents/index.js';
import { IFinanceDocumentsConfig } from './finance-documents/index.js';
import { IChecklistConfig } from './checklist/index.js';
import { IInterviewConfig } from './interview/index.js';

export interface SectionConfig {
  description?: string;
  label: string;
  summary?: string;
  summaryLabel?: string;
}

export interface IRegionConfigBase {
  configName: string;
  configDesc: string;
  type: RegionConfigType;
  docSectionStatus: SectionStatusType;
  finDocSectionStatus: SectionStatusType;
  interviewSectionStatus: SectionStatusType;
  historySectionStatus: SectionStatusType;
  financeSectionStatus: SectionStatusType;
  followUpDocSectionStatus: SectionStatusType;
  preDepartureDocSectionStatus?: SectionStatusType;
  afterTravelDocSectionStatus?: SectionStatusType;
  beforeArrivalDocAreaStatus: SectionStatusType;
  afterArrivalDocAreaStatus: SectionStatusType;
  beforeArrivalDocuments: IBeforeArrivalDocumentsConfig;
  afterArrivalDocuments: IAfterArrivalDocumentsConfig;
  scheduledInterviewsSectionStatus: SectionStatusType;
  documents: IDocumentConfig;
  financeDocuments: IFinanceDocumentsConfig;
  otherOptions: {
    visaSupport: OptionStatusType;
  };
  scheduledInterviews: IScheduledInterviewsConfig;
  customParentalConsentFormLink?: string;
  depositInstructionLink?: string;
  studentAutomatedChatReply: string;
  otherGenderEnabled?: boolean;
  universityAccommodationEnabled?: boolean;
  agentDetailsEnabled?: boolean;
  agentCompanyNameForStudentEnabled?: boolean;
}
export interface IRegionConfig extends IRegionConfigBase {
  id: string;
  recordedInterviewSectionStatus: SectionStatusType;
  Checklist: IChecklistConfig;
  sections: {
    CASChecklist: SectionConfig;
    FinanceDocuments: SectionConfig;
    GeneralDocument: SectionConfig;
    PreCASInterview: SectionConfig;
    ScheduledInterviews: SectionConfig;
    ReceiveCAS: SectionConfig;
    Registration: SectionConfig;
    VisaApplication: SectionConfig;
    FollowUpDocuments: SectionConfig;
  };
  Interview: IInterviewConfig;
  visaPrepActive?: boolean;
}

export interface IRegionConfigs {
  default: IRegionConfig;
  withAgent?: IRegionConfig;
}
export interface IRegionConfigForUpdate {
  regionConfigId: string;
  type?: string;
  regionName?: string;
  regionDescription?: string;
  nationalitySelection?: string[];
  onshore?: boolean;
  euss?: boolean;
  city?: string[];
  postcode?: string[];
  agentList?: string[];
  regionConfig?: IRegionConfigs;
  docType?: string;
}
export interface IRegionConfigWithFilters {
  id: string;
  regionConfig: IRegionConfigs;
  nationality?: Array<string>;
  isActive?: boolean;
  onshore?: boolean;
  euss?: boolean;
  city?: Array<string>;
  postcode?: Array<string>;
  agentList?: Array<string>;
  docType?: string;
}

export interface ITagsConfig {
  tags?: ITag[];
}

export interface IRegionConfigForStudent {
  docSectionStatus: SectionStatusType;
  finDocSectionStatus: SectionStatusType;
  interviewSectionStatus: SectionStatusType;
  agentDetailsEnabled?: boolean;
  agentCompanyNameForStudentEnabled?: boolean;
  otherOptions: {
    visaSupport: OptionStatusType;
  };
}

export interface IRegionConfigForDB {
  id: string;
  regionConfig: IRegionConfigs;
  nationality?: string;
  onshore?: boolean;
  euss?: boolean;
  city?: string[];
  postcode?: string[];
  agentList?: string[];
}

export interface IRegionConfigForSave {
  type: string;
  setupId: string;
  isSetupWithAgent: boolean;
  regionName: string;
  regionDescription: string;
  nationalitySelection: string[];
  onshore?: boolean;
  euss?: boolean;
  city?: string[];
  postcode?: string[];
  agentList?: string[];
}

export interface IDataCountry {
  alphaTwo: string;
  alphaThree: string;
  numeric: string;
  countryName: string;
  nationality: string;
  atas: boolean;
  tb: boolean;
  riskLevel: number;
  category: string;
  categoryNewNameRefactor?: string;
  region: string;
  // Finer-grained sub-region used by jurisdiction-specific calculators that
  // need to discriminate within a region — e.g. DHA travel costs split West
  // Africa (AUD 3,000) from East/Southern Africa (AUD 2,500). Only set for
  // countries the calculator needs to distinguish; absent ⇒ region-wide rate.
  subRegion?: 'WestAfrica' | 'EastSouthAfrica';
}

export enum CountryCategory {
  APPENDIX_H = 'AppendixH',
  NON_APPENDIX_H = 'NonAppendixH',
  STANDARD = 'Standard',
  DIFFERENTIAL = 'Differential (Low Risk)',
  EUSS = 'EUSS',
}

export * from './enums.js';
export * from './page-config/index.js';
export * from './input-field-config/index.js';
export * from './after-arrival-documents/index.js';
export * from './before-arrival-documents/index.js';
export * from './checklist/index.js';
export * from './documents/index.js';
export * from './finance-documents/index.js';
export * from './interview/index.js';
export * from './scheduled-interviews/index.js';
